/**
 * Phase 36 — a finished experiment: the result's sentence, both sides, and
 * "Apply the winner" — make the gateway first in the pool, take it out, or
 * give it its share by manual weights — each one confirmed, one PUT on the
 * gateway config. Or start another.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { DROP_GEN_EXPERIMENT_ACTIONS, type DropGenExperimentAction, type DropGenExperimentRow, EXPERIMENT_ACTION_WORDS, EXPERIMENT_METRIC_WORDS, experimentValue } from '@trafficpulse/shared';

import { Button, ConfirmDialog } from '@/components/ui';

import { EXPERIMENT_HONESTY, STATUS_WORDS, ddmm } from './experiment-copy';

interface Props {
  experiment: DropGenExperimentRow;
  label: string;
  containerName: string;
  canManage: boolean;
  busy: boolean;
  error: string | null;
  onApply: (action: DropGenExperimentAction) => void;
  onStartAnother: () => void;
}

const ACTION_HINT: Record<DropGenExperimentAction, string> = {
  first: 'Hold and Alternate start with it; the order becomes manual so it stays first.',
  out: 'It leaves the pool — the day never uses it again until you add it back.',
  share: 'Manual weights: the share it had, the others keep their proportions over the rest; a strategy without weights becomes a % split.',
};

export const ExperimentResult: FC<Props> = ({ experiment: e, label, containerName, canManage, busy, error, onApply, onStartAnother }) => {
  const [pending, setPending] = useState<DropGenExperimentAction | null>(null);
  const r = e.result;
  const applied = e.status === 'APPLIED';
  return (
    <div className="flex flex-col gap-3" data-testid="experiment-result">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <span>
          <b>{label}</b> · {e.sharePct} % of the drops · {EXPERIMENT_METRIC_WORDS[e.metric]} · {STATUS_WORDS[e.status]}
          {applied ? ` (${EXPERIMENT_ACTION_WORDS[e.appliedAction ?? 'first'](label, e.sharePct)})` : ''}
        </span>
        <span className="font-mono text-xs tabular-nums text-muted-foreground">
          {ddmm(e.startDate)} – {ddmm(e.endDate)}
        </span>
      </div>
      {r ? (
        <>
          <p className={r.tooEarly || r.winner === null ? 'text-sm text-warning' : 'text-sm'}>{r.words}</p>
          <div className="grid gap-2 text-xs sm:grid-cols-2">
            {[r.experiment, r.others].map((side) => (
              <div key={side.label} className="rounded-md border border-border/60 px-3 py-2">
                <div className="flex items-baseline justify-between">
                  <b>{side.label}</b>
                  <span className="font-mono tabular-nums">{experimentValue(r.metric, side.value)}</span>
                </div>
                <div className="text-muted-foreground">
                  {side.smsSent.toLocaleString('en-US')} SMS · {side.drops} drop{side.drops === 1 ? '' : 's'} · {side.conversions} conv. · {r.daysSeen} judged day{r.daysSeen === 1 ? '' : 's'}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-sm text-warning">No judged send in the period — nothing to compare.</p>
      )}
      <p className="text-[11px] text-muted-foreground">{EXPERIMENT_HONESTY}</p>
      <div className="flex flex-wrap items-center gap-2">
        {!applied && canManage
          ? DROP_GEN_EXPERIMENT_ACTIONS.map((action) => (
              <Button key={action} size="sm" variant={action === 'out' ? 'destructive' : 'outline'} disabled={busy} title={ACTION_HINT[action]} onClick={() => setPending(action)}>
                {capital(EXPERIMENT_ACTION_WORDS[action](label, e.sharePct))}
              </Button>
            ))
          : null}
        <Button size="sm" variant="ghost" disabled={busy || !canManage} onClick={onStartAnother}>
          Start another
        </Button>
        {error ? <span className="text-xs text-destructive">{error}</span> : null}
      </div>
      <ConfirmDialog
        open={pending !== null}
        title="Apply the winner?"
        description={pending ? `${capital(EXPERIMENT_ACTION_WORDS[pending](label, e.sharePct))} on ${containerName}'s gateway config — every container that follows it changes with it. ${ACTION_HINT[pending]}` : ''}
        confirmLabel="Apply"
        destructive={pending === 'out'}
        busy={busy}
        onConfirm={() => {
          if (pending) onApply(pending);
          setPending(null);
        }}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
